import { ErroApi } from '../../api';

/**
 * O PUT do anexo, com progresso e com como parar.
 *
 * O `fetch` não conta bytes enviados — só `XMLHttpRequest` tem `upload.onprogress`
 * — e com anexo de até 5 GiB (que leva minutos, ou horas) a barra é a única
 * coisa que diz à pessoa que o envio não travou. O mesmo objeto resolve o
 * outro lado: `abort()` fecha a conexão de verdade, e o servidor descarta o
 * que já tinha recebido (a API não grava arquivo parcial, 413 e abandono
 * incluídos). Com `fetch`, cancelar exigiria um `AbortController` e ainda
 * assim nenhum progresso.
 */

/** A pessoa (ou a tela, ao trocar de conversa) parou o envio. Não é falha. */
export class EnvioCancelado extends Error {
  constructor() {
    super('envio cancelado');
    this.name = 'EnvioCancelado';
  }
}

/**
 * A conexão caiu no meio. Distinta de uma recusa do servidor: aqui não houve
 * resposta nenhuma, e o conselho certo é tentar de novo, não ajustar o arquivo.
 */
export class EnvioSemRede extends Error {
  constructor() {
    super('a conexão caiu durante o envio');
    this.name = 'EnvioSemRede';
  }
}

export interface EnvioEmCurso {
  /**
   * Resolve quando o destino respondeu 2xx. Rejeita com `EnvioCancelado`,
   * `EnvioSemRede` ou `ErroApi` (resposta fora de 2xx, com o `codigo` do corpo
   * quando houver — 413 `tamanho_excedido`, 401 `token_expirado`).
   */
  promessa: Promise<void>;
  /** Para o envio e fecha a conexão. Sem efeito depois de terminado. */
  abortar: () => void;
}

/**
 * O corpo da recusa vira `ErroApi` com o mesmo desenho do `chamar`: a tela
 * escolhe a frase pelo código, e corpo ilegível cai em `erro`, que não casa
 * com nada e usa a frase padrão.
 */
function erroDaResposta(xhr: XMLHttpRequest): ErroApi {
  let corpo: { codigo?: unknown; mensagem?: unknown; detalhes?: unknown } = {};
  try {
    const lido: unknown = JSON.parse(xhr.responseText);
    if (typeof lido === 'object' && lido !== null) corpo = lido;
  } catch {
    // Corpo vazio ou HTML de um proxy: sem código, a frase padrão cobre.
  }
  return new ErroApi(
    xhr.status,
    typeof corpo.codigo === 'string' ? corpo.codigo : 'erro',
    typeof corpo.mensagem === 'string' ? corpo.mensagem : 'nao foi possivel completar a operacao',
    corpo.detalhes,
  );
}

/**
 * Sobe `arquivo` por `PUT` em `url`.
 *
 * `aoProgredir(enviados, total)` recebe o total do próprio arquivo, nunca o
 * `total` do evento: o navegador pode contar cabeçalho junto, e a barra tem de
 * fechar em 100% do ARQUIVO. Não há `timeout`: 5 GiB numa rede doméstica passa
 * de qualquer prazo razoável, e quem decide desistir é a pessoa, pelo botão.
 */
export function enviarComProgresso(
  url: string,
  arquivo: Blob,
  opcoes: { mime: string; aoProgredir: (enviados: number, total: number) => void },
): EnvioEmCurso {
  const xhr = new XMLHttpRequest();
  let encerrado = false;

  const promessa = new Promise<void>((resolver, rejeitar) => {
    const encerrar = (desfecho: () => void): void => {
      encerrado = true;
      desfecho();
    };

    xhr.upload.onprogress = (evento) => {
      if (encerrado) return;
      opcoes.aoProgredir(Math.min(evento.loaded, arquivo.size), arquivo.size);
    };
    xhr.onload = () =>
      encerrar(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          opcoes.aoProgredir(arquivo.size, arquivo.size);
          resolver();
          return;
        }
        rejeitar(erroDaResposta(xhr));
      });
    xhr.onerror = () => encerrar(() => rejeitar(new EnvioSemRede()));
    xhr.onabort = () => encerrar(() => rejeitar(new EnvioCancelado()));

    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', opcoes.mime);
    xhr.send(arquivo);
  });

  return {
    promessa,
    abortar: () => {
      if (!encerrado) xhr.abort();
    },
  };
}
